import {
  CampaignType,
  IMechanicConfig,
  IPrizeRow,
  ISlotSymbol,
  VoucherValidityMode,
} from "@/@types/gamification";

const isFilled = (v: unknown): boolean =>
  v !== undefined && v !== null && v !== "";

const toNumber = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function buildMechanicConfig(
  mc: Partial<IMechanicConfig> | undefined,
  mechanic: CampaignType,
): IMechanicConfig | undefined {
  const source = mc ?? {};
  const out: IMechanicConfig = {};

  if (Array.isArray(source.prizes) && source.prizes.length > 0) {
    out.prizes = source.prizes.map(
      (p): IPrizeRow => ({
        value: toNumber(p.value),
        probability: toNumber(p.probability),
        ...(isFilled(p.label) ? { label: p.label } : {}),
      }),
    );
  }

  if (isFilled(source.maxPlays)) out.maxPlays = toNumber(source.maxPlays);
  if (isFilled(source.minPurchase)) out.minPurchase = toNumber(source.minPurchase);
  if (isFilled(source.priority)) out.priority = toNumber(source.priority);

  if (source.concurrency) {
    const competes = Boolean(source.concurrency.competes);
    out.concurrency = {
      competes,
      group: competes ? String(source.concurrency.group ?? "").trim() : null,
    };
  }

  if (source.voucherValidity?.mode) {
    const mode = source.voucherValidity.mode;
    out.voucherValidity = {
      mode,
      ...(mode === VoucherValidityMode.DAYS &&
      isFilled(source.voucherValidity.validityDays)
        ? { validityDays: toNumber(source.voucherValidity.validityDays) }
        : {}),
      ...(isFilled(source.voucherValidity.startOffsetDays)
        ? { startOffsetDays: toNumber(source.voucherValidity.startOffsetDays) }
        : {}),
    };
  }

  if (source.cpfSegmentation) {
    out.cpfSegmentation = { enabled: Boolean(source.cpfSegmentation.enabled) };
  }

  const mechanicParams: IMechanicConfig["mechanicParams"] = {};
  if (
    mechanic === CampaignType.PENALTY &&
    isFilled(source.mechanicParams?.goalProbability)
  ) {
    mechanicParams.goalProbability = toNumber(
      source.mechanicParams?.goalProbability,
    );
  }
  if (
    mechanic === CampaignType.SLOT &&
    Array.isArray(source.mechanicParams?.symbols)
  ) {
    mechanicParams.symbols = source.mechanicParams!.symbols!.map(
      (s): ISlotSymbol => ({
        id: s.id,
        label: s.label,
        value: toNumber(s.value),
        ...(isFilled(s.icon) ? { icon: s.icon } : {}),
      }),
    );
  }
  if (Object.keys(mechanicParams).length) out.mechanicParams = mechanicParams;

  if (source.presentation) {
    out.presentation = {
      customerFacingName: String(
        source.presentation.customerFacingName ?? "",
      ).trim(),
      customerFacingDescription: String(
        source.presentation.customerFacingDescription ?? "",
      ).trim(),
    };
  }

  return Object.keys(out).length ? out : undefined;
}

export interface ConcurrencyPeriodParams {
  competes: boolean;
  group?: string;
}

export function concurrencyPeriodParams(
  mc: Partial<IMechanicConfig> | undefined,
): ConcurrencyPeriodParams {
  const competes = Boolean(mc?.concurrency?.competes);
  const group = String(mc?.concurrency?.group ?? "").trim();
  if (!competes || !group) return { competes };
  return { competes, group };
}
